import { useEffect, useRef } from "react";
import { create } from "zustand";

interface AddMeetingResetState {
  /**
   * Monotonic counter per route path. Bumped each time the user clicks an
   * "Add meeting" menu item so a page already mounted on that route can reset
   * itself to a fresh state — `router.push` to the current route is a no-op in
   * the App Router, so the page would otherwise keep its stale internal state.
   */
  signals: Record<string, number>;
  requestReset: (path: string) => void;
}

export const useAddMeetingResetStore = create<AddMeetingResetState>((set) => ({
  signals: {},
  requestReset: (path) =>
    set((state) => ({
      signals: { ...state.signals, [path]: (state.signals[path] ?? 0) + 1 },
    })),
}));

/**
 * Runs `onReset` when the user re-triggers the "Add meeting" navigation for
 * `path` while the page is already mounted. Does NOT fire on initial mount:
 * the signal is bumped before the route changes, so a freshly mounted page
 * starts in sync and only reacts to subsequent re-clicks.
 */
export const useAddMeetingReset = (path: string, onReset: () => void) => {
  const signal = useAddMeetingResetStore((s) => s.signals[path] ?? 0);
  const previousSignal = useRef(signal);
  const onResetRef = useRef(onReset);

  // Keep the latest callback without retriggering the signal effect below.
  useEffect(() => {
    onResetRef.current = onReset;
  });

  useEffect(() => {
    if (signal !== previousSignal.current) {
      previousSignal.current = signal;
      onResetRef.current();
    }
  }, [signal]);
};
